import { msg } from "../../../locales/translate.ts";
export const BAN_ENTITY_TYPES = ["email", "card", "country", "ip", "bin", "iban"] as const;
export const BAN_TARGET_TYPES = ["project", "client", "merchant"] as const;
export type BanEntityType = typeof BAN_ENTITY_TYPES[number];
export type BanTargetType = typeof BAN_TARGET_TYPES[number];
export const BAN_FILTER_FIELDS = ["id", "target_type", "target_id", "client_id", "entity_type", "entity_value", "user_id", "search", "from", "to"] as const;
export type BanFilters = Record<typeof BAN_FILTER_FIELDS[number], string>;
export const defaultBanFilters = (): BanFilters => ({ id: "", target_type: "", target_id: "", client_id: "", entity_type: "", entity_value: "", user_id: "", search: "", from: "", to: "" });
export interface BanDraft { entity_type: BanEntityType; entity_value: string; target_type: BanTargetType; target_id: string; reason: string; display_reason: string; }
export const defaultBanDraft = (projectId: number): BanDraft => ({ entity_type: "email", entity_value: "", target_type: "project", target_id: String(projectId), reason: "", display_reason: "" });
export type BanRecord = Record<string, unknown>;
const record = (value: unknown): value is BanRecord => !!value && typeof value === "object" && !Array.isArray(value);

// HTTP may return either an unwrapped payload or the API's { data, error } envelope.
export const readBanResponse = (response: unknown): { data?: unknown; error?: string } => {
	if (!record(response)) return {};
	let envelope = response;
	for (let depth = 0; depth < 2; depth++) {
		if (envelope.error && (!record(envelope.error) || envelope.error.code !== 0)) {
			return { error: record(envelope.error) && typeof envelope.error.message === "string" && envelope.error.message ? envelope.error.message : msg("requestFailed") };
		}
		if (depth === 0 && record(envelope.data) && ("data" in envelope.data || "error" in envelope.data)) envelope = envelope.data;
		else return { data: envelope.data };
	}
	return {};
};

export const banId = (value: unknown): number | null => {
	if (typeof value !== "number" && (typeof value !== "string" || !/^\d+$/.test(value))) return null;
	const id = Number(value);
	return Number.isSafeInteger(id) && id > 0 ? id : null;
};
const idOrThrow = (value: unknown) => { const id = banId(value); if (id === null) throw new Error("invalidId"); return id; };
const validDate = (value: string) => /^\d{4}-\d{2}-\d{2}$/.test(value) && Number.isFinite(Date.parse(value)) && new Date(value).toISOString().slice(0, 10) === value;
const request = (method: string, projectId: number, data: Record<string, unknown>) => ({ method, expectedProjectId: idOrThrow(projectId), headers: { ignoreBusy: "true" }, data });

export const getBanListRequest = (projectId: number, filters: BanFilters, page: number, perPage: number) => {
	const data: Record<string, unknown> = {};
	for (const field of BAN_FILTER_FIELDS) {
		const value = filters[field].trim();
		if (!value) continue;
		data[field] = ["id", "target_id", "client_id", "user_id"].includes(field) ? idOrThrow(value) : value;
	}
	if (data.entity_type && !BAN_ENTITY_TYPES.includes(data.entity_type as BanEntityType) || data.target_type && !BAN_TARGET_TYPES.includes(data.target_type as BanTargetType)) throw new Error("invalidFilters");
	if (filters.from && !validDate(filters.from) || filters.to && !validDate(filters.to) || filters.from && filters.to && filters.from > filters.to) throw new Error("invalidDates");
	if (!Number.isSafeInteger(page) || page < 0 || ![25, 50, 100].includes(perPage)) throw new Error("invalidFilters");
	return request("banlist/list", projectId, { ...data, pagination: { page, perPage } });
};

export const getBanSetRequest = (projectId: number, draft: BanDraft) => {
	if (!BAN_ENTITY_TYPES.includes(draft.entity_type) || !BAN_TARGET_TYPES.includes(draft.target_type)) throw new Error("invalidFilters");
	const entityValue = draft.entity_value.trim();
	if (!entityValue || /[\u0000-\u001f\u007f]/.test(entityValue)) throw new Error("invalidEntity");
	// Project bans always apply to the active project. Client/merchant bans need an explicit scope.
	const targetId = draft.target_type === "project" ? idOrThrow(projectId) : idOrThrow(draft.target_id.trim());
	return request("banlist/set", projectId, {
		entity_type: draft.entity_type, entity_value: entityValue, target_type: draft.target_type, target_id: targetId,
		...(draft.reason.trim() ? { reason: draft.reason.trim() } : {}), ...(draft.display_reason.trim() ? { display_reason: draft.display_reason.trim() } : {}),
	});
};

export const getBanRemoveRequest = (projectId: number, id: unknown) => request("banlist/remove", projectId, { id: idOrThrow(id) });

export interface BanList { rows: BanRecord[]; total: number | null; totalPages: number | null; }
const count = (value: unknown) => (typeof value === "number" || typeof value === "string" && /^\d+$/.test(value)) && Number.isSafeInteger(Number(value)) && Number(value) >= 0 ? Number(value) : null;

// Until the response is documented, accept list envelopes without discarding unknown record fields.
export const parseBanList = (payload: unknown): BanList | null => {
	if (record(payload) && record(payload.data)) payload = payload.data;
	const rows = Array.isArray(payload) ? payload : record(payload) ? [payload.items, payload.rows, payload.list, payload.data].find(Array.isArray) : null;
	if (!Array.isArray(rows) || !rows.every(record)) return null;
	const pagination = record(payload) && record(payload.pagination) ? payload.pagination : null;
	return { rows, total: count(pagination?.total), totalPages: count(pagination?.totalPages) };
};

export const banText = (value: unknown): string => typeof value === "string" && value !== "" || typeof value === "number" ? String(value) : "—";
